import { isObject } from '@pineapplelab/util';
import { Timestamp } from 'firebase/firestore';
import firebase from 'firebase/compat/app';
import { CategoryEnrollment } from '../../models/categoryEnrollment';
import { CourseEnrollment } from '../../models/courseEnrollment';
import { AssignedCategory } from '../../models/assignedCategory';
import { Category } from '../../models/category';

/**
 * The server-maintained read model behind My Courses: one document per learner holding every
 * category and course the page shows, already joined with the category flag and the client's
 * orientation-form settings. Mirrors `MyCoursesSummaryDoc` in arla-cms-back.
 */
export const MY_COURSES_SUMMARY_VERSION = 1;

export namespace MyCoursesSummary {
	export const PATH = 'summaries';
	export const DOC_ID = 'myCourses';
}

export function isCurrentSummary(data: any): boolean {
	return data?.version === MY_COURSES_SUMMARY_VERSION && isObject(data?.categories);
}

/**
 * Turns the summary into the same `CategoryEnrollment` graph the page rendered from the raw
 * enrollments, so nothing downstream (cards, favourites, access rules) has to know which source
 * the data came from. The keyed maps are unpacked here rather than handed to `fromJson`: its
 * snake→camel walk would rename map keys that happen to contain underscores.
 */
export function mapSummaryToCategoryEnrollments(data: any): CategoryEnrollment[] {
	const categories = isObject(data?.categories) ? data.categories : {};

	return Object.keys(categories).map(categoryId => {
		const {
			courses,
			category_enforce_course_order,
			form_link,
			require_form_first,
			...enrollmentJson
		} = categories[categoryId] ?? {};

		const enrollment = new CategoryEnrollment({ ...enrollmentJson, id: categoryId });
		if (enrollment.languages?.length === 1 || enrollment.selectedLanguage) {
			enrollment.translated = true;
		}

		const coursesMap = isObject(courses) ? courses : {};
		enrollment.__coursesEnrollments = Object.keys(coursesMap).map(courseId => {
			const { completed_chapters, ...courseJson } = coursesMap[courseId] ?? {};
			const course = new CourseEnrollment({ ...courseJson, id: courseId });
			course.__completedChapters = Number(completed_chapters) || 0;
			return course;
		});

		enrollment.__assignedCategory = new AssignedCategory({
			id: categoryId,
			form_link: form_link ?? null,
			require_form_first: require_form_first === true
		});
		enrollment.__category = new Category({
			id: categoryId,
			enforce_course_order: category_enforce_course_order === true
		});

		return enrollment;
	});
}

// ---------------------------------------------------------------------------------------------
// Local persistence. The last summary a learner saw is kept in localStorage so a returning learner
// gets their courses painted before the network answers; the live snapshot then replaces it.
// ---------------------------------------------------------------------------------------------

const STORAGE_PREFIX = 'arla:myCoursesSummary:';

export function summaryStorageKey(projectId: string, userId: string): string {
	return `${STORAGE_PREFIX}${projectId}:${userId}`;
}

/**
 * Firestore values don't survive JSON; tag them so `deserializeSummary` can rebuild them. The tree
 * is converted before stringifying rather than in a replacer: `JSON.stringify` calls a value's
 * `toJSON()` before the replacer sees it, which turns a Timestamp into a plain object too early.
 */
export function serializeSummary(data: any): string {
	return JSON.stringify(toStorable(data));
}

function toStorable(value: any): any {
	if (value === null || value === undefined) {
		return value ?? null;
	}
	if (typeof value.toMillis === 'function' && typeof value.seconds === 'number') {
		return { __ts: [value.seconds, value.nanoseconds ?? 0] };
	}
	if (typeof value.path === 'string' && typeof value.get === 'function') {
		return { __ref: value.path };
	}
	if (Array.isArray(value)) {
		return value.map(toStorable);
	}
	if (typeof value === 'object') {
		const out: { [key: string]: any } = {};
		for (const key of Object.keys(value)) {
			out[key] = toStorable(value[key]);
		}
		return out;
	}
	return value;
}

export function deserializeSummary(raw: string, firestore: firebase.firestore.Firestore): any {
	return JSON.parse(raw, (_key, value) => {
		if (Array.isArray(value?.__ts)) {
			return new Timestamp(value.__ts[0], value.__ts[1]);
		}
		if (typeof value?.__ref === 'string') {
			return firestore.doc(value.__ref);
		}
		return value;
	});
}

export function readStoredSummary(projectId: string, userId: string, firestore: firebase.firestore.Firestore): any | null {
	try {
		const raw = localStorage.getItem(summaryStorageKey(projectId, userId));
		if (!raw) {
			return null;
		}
		const data = deserializeSummary(raw, firestore);
		return isCurrentSummary(data) ? data : null;
	} catch {
		return null;
	}
}

export function writeStoredSummary(projectId: string, userId: string, data: any): void {
	try {
		localStorage.setItem(summaryStorageKey(projectId, userId), serializeSummary(data));
	} catch {
		// Quota or private mode: the cache is a convenience, never a requirement.
	}
}

/** Drops every stored summary; called on logout so a shared device doesn't keep the last learner's courses. */
export function clearStoredSummaries(): void {
	try {
		const keys: string[] = [];
		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i);
			if (key?.startsWith(STORAGE_PREFIX)) {
				keys.push(key);
			}
		}
		keys.forEach(key => localStorage.removeItem(key));
	} catch {
		// Nothing to clear, or storage unavailable.
	}
}
